import React from 'react';
import { X, Search, Calendar, Filter, Package, ChevronLeft, Loader2, Save, ArrowLeft } from 'lucide-react';
import { useStore } from '../store';
import { translations } from '../types';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';

interface ShipmentHistoryProps {
  onClose: () => void;
}

interface TrackingNumber {
  tracking_number: string;
  child_trackings?: string[];
}

interface Shipment {
  id: string;
  shipment_number: string;
  tracking_numbers: TrackingNumber[] | string;
  boxes: Array<{
    weight: number;
    width: number;
    height: number;
    depth: number;
  }>;
  total_weight: number;
  total_volume: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export function ShipmentHistory({ onClose }: ShipmentHistoryProps) {
  const { language } = useStore();
  const t = translations[language];
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [showList, setShowList] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedBoxes, setEditedBoxes] = useState<Array<{
    weight: number;
    width: number;
    height: number;
    depth: number;
  }> | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadShipments();
  }, [statusFilter, dateFilter]);

  useEffect(() => {
    if (selectedShipment) {
      setEditedBoxes(selectedShipment.boxes);
      setIsEditing(false);
    }
  }, [selectedShipment]);

  const saveChanges = async () => {
    if (!selectedShipment || !editedBoxes) return;
    
    try {
      setIsSaving(true);
      
      const totalWeight = editedBoxes.reduce((sum, box) => sum + box.weight, 0);
      const totalVolume = editedBoxes.reduce((sum, box) => {
        return sum + ((box.width * box.height * box.depth) / 1000000);
      }, 0);
      const density = totalVolume > 0 ? totalWeight / totalVolume : 0;

      // Update shipment in database
      const { error } = await supabase
        .from('shipments')
        .update({
          boxes: editedBoxes,
          total_weight: totalWeight,
          total_volume: totalVolume,
          density: density,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedShipment.id);

      if (error) throw error;

      // Update local state
      setShipments(prevShipments => 
        prevShipments.map(shipment => 
          shipment.id === selectedShipment.id 
            ? {
                ...shipment,
                boxes: editedBoxes,
                total_weight: totalWeight,
                total_volume: totalVolume,
                updated_at: new Date().toISOString()
              }
            : shipment
        )
      );

      setSelectedShipment(prev => prev ? {
        ...prev,
        boxes: editedBoxes,
        total_weight: totalWeight,
        total_volume: totalVolume,
        updated_at: new Date().toISOString()
      } : null);

      setIsEditing(false);

    } catch (error) {
      console.error('Error updating shipment:', error);
      alert('Ошибка при обновлении данных');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBoxChange = (index: number, field: keyof Shipment['boxes'][0], value: number) => {
    if (!editedBoxes || !selectedShipment || !isEditing) return;

    const newBoxes = editedBoxes.map((box, i) => 
      i === index ? { ...box, [field]: value } : box
    );

    setEditedBoxes(newBoxes);
  };

  // Parse tracking numbers safely from different formats
  const parseTrackingNumbers = (trackingData: any): TrackingNumber[] => {
    try {
      // Handle string input (JSON string)
      if (typeof trackingData === 'string') {
        try {
          return JSON.parse(trackingData);
        } catch (e) {
          console.error('Error parsing tracking numbers string:', e);
          return [];
        }
      }
      
      // Handle array input
      if (Array.isArray(trackingData)) {
        return trackingData.filter(item => item && typeof item === 'object');
      }
      
      // Handle object input
      if (trackingData && typeof trackingData === 'object') {
        if (trackingData.tracking_number) {
          return [trackingData];
        }
        return Object.values(trackingData).filter(item => item && typeof item === 'object');
      }
      
      // Default case: return empty array
      return [];
    } catch (error) {
      console.error('Error parsing tracking numbers:', error);
      return [];
    }
  };

  const loadShipments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let query = supabase
        .from('shipments')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query;

      if (error) throw error;

      setShipments(data || []);
    } catch (err) {
      console.error('Error loading shipments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load shipments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShipmentClick = (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setShowList(false);
    window.history.pushState(null, '', `/shipment-history/${shipment.shipment_number}`);
  };

  const handleBack = () => {
    if (!showList) {
      setShowList(true);
      setSelectedShipment(null);
      window.history.pushState(null, '', '/shipment-history');
    } else {
      onClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredShipments = shipments.filter(shipment => {
    // Handle search query
    let matchesSearch = searchQuery === '' || 
      shipment.shipment_number.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Search in tracking numbers
    if (!matchesSearch && shipment.tracking_numbers) {
      const trackingNumberItems = parseTrackingNumbers(shipment.tracking_numbers);
      
      matchesSearch = trackingNumberItems.some(item => 
        item.tracking_number && 
        item.tracking_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by date
    const shipmentDate = new Date(shipment.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = shipmentDate.toDateString() === today.toDateString();
    const isYesterday = shipmentDate.toDateString() === yesterday.toDateString();

    const matchesDate = dateFilter === 'all' ||
      (dateFilter === 'today' && isToday) ||
      (dateFilter === 'yesterday' && isYesterday);

    return matchesSearch && matchesDate;
  });

  const ShipmentDetails = ({ shipment }: { shipment: Shipment }) => {
    // Safely parse tracking numbers
    const trackingNumbers = parseTrackingNumbers(shipment.tracking_numbers);
    
    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-4 py-6">
            {/* Status */}
            <div className="mb-6">
              <div className="text-sm text-gray-500 mb-1">{t.status}</div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm ${getStatusColor(shipment.status)}`}> 
                {t.sent}
              </div>
            </div>

            {/* Dates */}
            <div className="mb-6">
              <div className="text-sm text-gray-500 mb-1">{t.dates}</div>
              <div className="space-y-1">
                <div>
                  <span className="text-sm text-gray-500">{t.created}: </span>
                  <span className="font-medium">
                    {new Date(shipment.created_at).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">{t.updated}: </span>
                  <span className="font-medium">
                    {new Date(shipment.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Boxes */}
            <div className="mb-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-500">{t.boxes}</div>
                {isEditing ? (
                  <button
                    onClick={saveChanges}
                    disabled={isSaving}
                    className={`px-3 py-1 rounded text-sm flex items-center gap-1.5 ${
                      isSaving ? 'bg-gray-100 text-gray-400' : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t.saving}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {t.save}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200"
                  >
                    {t.edit}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {Array.isArray(isEditing ? editedBoxes : shipment.boxes) && 
                (isEditing ? editedBoxes : shipment.boxes)?.map((box, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                    <div className="font-medium mb-2">{t.box} {index + 1}</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="block text-gray-500 mb-1">{t.weight} ({language === 'ru' ? 'кг' : '千克'})</label>
                        <input
                          type="number"
                          value={box.weight || ''}
                          onChange={(e) => handleBoxChange(index, 'weight', parseFloat(e.target.value) || 0)}
                          className={`w-full px-2 py-1 rounded text-right ${isEditing ? 'border' : 'border-0 bg-transparent'}`}
                          disabled={!isEditing}
                          min="0"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <div className="text-gray-500 mb-1">{t.volume}</div>
                        <div className="text-right">
                          {((box.width * box.height * box.depth) / 1000000).toFixed(3)} {language === 'ru' ? 'м³' : '立方米'}
                        </div>
                      </div>
                      <div className="col-span-2 grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-gray-500 mb-1">{t.width} ({language === 'ru' ? 'см' : '厘米'})</label>
                          <input
                            type="number"
                            value={box.width || ''}
                            onChange={(e) => handleBoxChange(index, 'width', parseFloat(e.target.value) || 0)}
                            className={`w-full px-2 py-1 rounded text-right ${isEditing ? 'border' : 'border-0 bg-transparent'}`}
                            disabled={!isEditing}
                            min="0"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-1">{t.height} ({language === 'ru' ? 'см' : '厘米'})</label>
                          <input
                            type="number"
                            value={box.height || ''}
                            onChange={(e) => handleBoxChange(index, 'height', parseFloat(e.target.value) || 0)}
                            className={`w-full px-2 py-1 rounded text-right ${isEditing ? 'border' : 'border-0 bg-transparent'}`}
                            disabled={!isEditing}
                            min="0"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-1">{t.depth} ({language === 'ru' ? 'см' : '厘米'})</label>
                          <input
                            type="number"
                            value={box.depth || ''}
                            onChange={(e) => handleBoxChange(index, 'depth', parseFloat(e.target.value) || 0)}
                            className={`w-full px-2 py-1 rounded text-right ${isEditing ? 'border' : 'border-0 bg-transparent'}`}
                            disabled={!isEditing}
                            min="0"
                            step="0.1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="font-medium mb-2">{t.totals}</div>
              <div className="space-y-1">
                <div>
                  <span className="text-gray-600">{t.trackingNumberCount}: </span>
                  <span className="font-medium">
                    {trackingNumbers.reduce((total, track) => {
                      if (!track) return total;
                      const childCount = Array.isArray(track.child_trackings) 
                        ? track.child_trackings.filter(Boolean).length 
                        : 0;
                      return total + 1 + childCount;
                    }, 0)} {t.pieces}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">{t.totalWeight}: </span>
                  <span className="font-medium">{shipment.total_weight} {language === 'ru' ? 'кг' : '千克'}</span>
                </div>
                <div>
                  <span className="text-gray-600">{t.totalVolume}: </span>
                  <span className="font-medium">{shipment.total_volume} {language === 'ru' ? 'м³' : '立方米'}</span>
                </div>
                <div>
                  <span className="text-gray-600">{t.density}: </span>
                  <span className="font-medium">
                    {(shipment.total_weight / (shipment.total_volume || 1)).toFixed(2)} {language === 'ru' ? 'кг/м³' : '千克/立方米'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tracking Numbers */}
            <div className="mt-6">
              <div className="text-sm text-gray-500 mb-2">{t.trackingNumbers}</div>
              <div className="space-y-2">
                {trackingNumbers.length === 0 ? (
                  <div className="text-sm text-gray-500">No tracking numbers found</div>
                ) : (
                  trackingNumbers.map((track, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-mono">
                        {track.tracking_number}
                        {Array.isArray(track.child_trackings) && 
                         track.child_trackings.filter(Boolean).length > 0 && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            Parent
                          </span>
                        )}
                      </div>
                      {Array.isArray(track.child_trackings) && 
                       track.child_trackings.filter(Boolean).length > 0 && (
                        <div className="mt-2 pl-4 space-y-1 border-l-2 border-gray-200">
                          {track.child_trackings
                            .filter(Boolean)
                            .map((childTracking, childIndex) => (
                              <div key={childIndex} className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-gray-300 rounded-full" />
                                <div className="font-mono text-sm text-gray-600">
                                  {childTracking}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
        </div>
      </div>
    );
  };

  return (
    <div className="absolute inset-0 bg-white z-50 overflow-hidden flex flex-col">
      <div className="bg-white shadow px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">
            {showList ? t.shipmentHistory : selectedShipment?.shipment_number}
          </h1>
        </div>
      </div>
      
      {showList ? (
        <>
      <div className="p-4 border-b sticky top-14 bg-white z-[5]">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-gray-700">{t.filter}</span>
          </div>
          <span className="text-sm text-gray-500">
            {filteredShipments.length} / {shipments.length}
          </span>
        </button>

        {showFilters && (
          <div className="mt-2 space-y-2 bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full bg-transparent border-none focus:ring-0 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="flex-1 bg-gray-50 border-none rounded-lg text-sm focus:ring-0"
                >
                  <option value="all">{t.allDates}</option>
                  <option value="today">{t.today}</option>
                  <option value="yesterday">{t.yesterday}</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 bg-gray-50 border-none rounded-lg text-sm focus:ring-0"
                >
                  <option value="all">Все статусы</option>
                  <option value="created">{t.sent}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600">{error}</div>
        ) : filteredShipments.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchQuery ? t.nothingFound : t.noShipments}
          </div>
        ) : (
          <div className="divide-y">
            {filteredShipments.map((shipment) => (
              <div 
                key={shipment.id} 
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleShipmentClick(shipment)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="font-medium">{shipment.shipment_number}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(shipment.created_at).toLocaleString(
                          language === 'ru' ? 'ru-RU' : 'zh-CN'
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(shipment.status)}`}>
                      {t.sent}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      ) : selectedShipment ? (
        <ShipmentDetails shipment={selectedShipment} />
      ) : null}
    </div>
  );
}